import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Shield,
  ShieldOff,
  ShieldCheck,
  AlertTriangle,
  XCircle,
  Ban,
  AlertCircle,
  Loader2,
  RefreshCw,
  CheckCircle2,
  ChevronDown,
  Trash2,
  Eye,
} from "lucide-react";
import { colors, fonts } from "../styles/theme";
import {
  fetchEmailHealthSummary,
  fetchEmailHealthContacts,
  suppressContact,
  clearContactFlags,
  bulkEmailHealthAction,
} from "../services/api";
import { Badge } from "../components/ui";

const FILTERS = [
  {
    value: "active",
    label: "Needs Action",
    icon: AlertTriangle,
    color: colors.warning,
  },
  {
    value: "all",
    label: "All Problems",
    icon: AlertCircle,
    color: colors.textSecondary,
  },
  { value: "hard", label: "Hard Bounces", icon: XCircle, color: colors.danger },
  {
    value: "soft",
    label: "Soft Bounces (3+)",
    icon: AlertTriangle,
    color: "#f59e0b",
  },
  {
    value: "spam",
    label: "Spam Reports",
    icon: ShieldOff,
    color: colors.danger,
  },
  { value: "dropped", label: "Dropped", icon: Ban, color: colors.danger },
  {
    value: "suppressed",
    label: "Suppressed",
    icon: ShieldCheck,
    color: colors.success,
  },
];

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function ProblemBadges({ contact }) {
  const badges = [];
  if (contact.bounceStatus === "hard") {
    badges.push(
      <Badge
        key="hb"
        color={colors.danger}
        bg={`${colors.danger}18`}
        title={contact.lastBounceReason || "Hard bounce"}
      >
        <XCircle size={10} /> Hard Bounce
      </Badge>,
    );
  }
  if ((contact.softBounceCount || 0) >= 3 && contact.bounceStatus !== "hard") {
    badges.push(
      <Badge
        key="sb"
        color="#f59e0b"
        bg="rgba(245,158,11,0.1)"
        title={contact.lastSoftBounceReason}
      >
        <AlertTriangle size={10} /> {contact.softBounceCount} Soft Bounces
      </Badge>,
    );
  }
  if (contact.spamReported) {
    badges.push(
      <Badge
        key="sp"
        color={colors.danger}
        bg={`${colors.danger}18`}
        title={`Reported ${fmtDate(contact.spamReportedAt)}`}
      >
        <ShieldOff size={10} /> Spam
      </Badge>,
    );
  }
  if (contact.emailDropped) {
    badges.push(
      <Badge
        key="dr"
        color={colors.danger}
        bg={`${colors.danger}18`}
        title={contact.dropReason}
      >
        <Ban size={10} /> Dropped
      </Badge>,
    );
  }
  if (contact.emailSuppressed) {
    badges.push(
      <Badge
        key="su"
        color={colors.success}
        bg={`${colors.success}18`}
        title={`Suppressed by ${contact.suppressedBy || "system"} — ${contact.suppressedReason || ""}`}
      >
        <ShieldCheck size={10} /> Suppressed
      </Badge>,
    );
  }
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{badges}</div>
  );
}

export default function EmailHealth() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("active");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); // contactId being acted on
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryData, contactsData] = await Promise.all([
        fetchEmailHealthSummary(),
        fetchEmailHealthContacts({ filter, limit: 200 }),
      ]);
      setSummary(summaryData);
      setContacts(contactsData.contacts);
      setTotal(contactsData.total);
      setSelected(new Set());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-clear success message
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  const handleSuppress = async (contact) => {
    setActionLoading(contact.id);
    try {
      await suppressContact(contact.id, contact.email);
      setSuccess(
        `${contact.email} has been suppressed — no more emails will be sent`,
      );
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleClear = async (contact) => {
    setActionLoading(contact.id);
    try {
      await clearContactFlags(contact.id, contact.email);
      setSuccess(`All flags cleared for ${contact.email}`);
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkAction = async (operation) => {
    setBulkMenuOpen(false);
    if (selected.size === 0) return;
    setActionLoading("bulk");
    try {
      const list = contacts
        .filter((c) => selected.has(c.id))
        .map((c) => ({ contactId: c.id, email: c.email }));
      const result = await bulkEmailHealthAction(list, operation);
      setSuccess(
        `Bulk ${operation}: ${result.success} contacts updated${result.failed ? `, ${result.failed} failed` : ""}`,
      );
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === contacts.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(contacts.map((c) => c.id)));
    }
  };

  const summaryCards = summary
    ? [
        {
          label: "Hard Bounces",
          value: summary.hardBounce,
          icon: XCircle,
          color: colors.danger,
        },
        {
          label: "Soft Bounces (3+)",
          value: summary.softBounce,
          icon: AlertTriangle,
          color: "#f59e0b",
        },
        {
          label: "Spam Reports",
          value: summary.spamReport,
          icon: ShieldOff,
          color: colors.danger,
        },
        {
          label: "Dropped",
          value: summary.dropped,
          icon: Ban,
          color: colors.danger,
        },
        {
          label: "Suppressed",
          value: summary.suppressed,
          icon: ShieldCheck,
          color: colors.success,
        },
      ]
    : [];

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 24,
        }}
      >
        <button
          onClick={() => navigate("/newsletters")}
          style={{
            background: "none",
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            padding: "6px 10px",
            cursor: "pointer",
            color: colors.textSecondary,
            display: "flex",
            alignItems: "center",
          }}
        >
          <ArrowLeft size={16} />
        </button>
        <div style={{ flex: 1 }}>
          <h1
            style={{
              fontFamily: fonts.heading,
              fontSize: 24,
              fontWeight: 700,
              color: colors.text,
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Shield size={22} color={colors.accent} />
            Email Health
          </h1>
          <p
            style={{
              fontFamily: fonts.body,
              fontSize: 13,
              color: colors.textSecondary,
              margin: "2px 0 0",
            }}
          >
            Monitor and neutralise email delivery problems
          </p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          style={{
            padding: "8px 16px",
            background: "none",
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            cursor: loading ? "not-allowed" : "pointer",
            color: colors.textSecondary,
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
          }}
        >
          <RefreshCw
            size={14}
            className={loading ? "spin" : ""}
          />
          Refresh
        </button>
      </div>

      {/* Success / Error banners */}
      {success && (
        <div
          style={{
            padding: "10px 16px",
            background: `${colors.success}12`,
            border: `1px solid ${colors.success}30`,
            borderRadius: 8,
            marginBottom: 16,
            color: colors.success,
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <CheckCircle2 size={16} />
          {success}
        </div>
      )}
      {error && (
        <div
          style={{
            padding: "10px 16px",
            background: `${colors.danger}10`,
            border: `1px solid ${colors.danger}30`,
            borderRadius: 8,
            marginBottom: 16,
            color: colors.danger,
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <AlertTriangle size={16} />
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: "auto",
              background: "none",
              border: "none",
              color: colors.danger,
              cursor: "pointer",
              fontSize: 16,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Summary cards */}
      {summary && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 12,
            marginBottom: 24,
          }}
        >
          {summaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.label}
                style={{
                  background: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  padding: "16px 20px",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: `${card.color}14`,
                  }}
                >
                  <Icon size={18} color={card.color} />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      fontFamily: fonts.heading,
                      color: colors.text,
                    }}
                  >
                    {card.value}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: colors.textSecondary,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {card.label}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filter bar + bulk actions */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        {FILTERS.map((f) => {
          const Icon = f.icon;
          const isActive = filter === f.value;
          return (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              style={{
                padding: "6px 14px",
                background: isActive ? `${f.color}18` : "transparent",
                border: `1px solid ${isActive ? f.color : colors.border}`,
                borderRadius: 6,
                fontSize: 12,
                fontWeight: isActive ? 600 : 400,
                color: isActive ? f.color : colors.textSecondary,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 5,
                transition: "all 0.15s",
              }}
            >
              <Icon size={12} />
              {f.label}
            </button>
          );
        })}

        <div style={{ flex: 1 }} />

        {/* Bulk actions */}
        {selected.size > 0 && (
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setBulkMenuOpen(!bulkMenuOpen)}
              style={{
                padding: "6px 14px",
                background: colors.accent,
                color: "#fff",
                border: "none",
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              {actionLoading === "bulk" ? (
                <Loader2
                  size={12}
                  className="spin"
                />
              ) : null}
              {selected.size} selected
              <ChevronDown size={12} />
            </button>
            {bulkMenuOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: 4,
                  background: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  zIndex: 100,
                  minWidth: 180,
                  overflow: "hidden",
                }}
              >
                <button
                  onClick={() => handleBulkAction("suppress")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "10px 14px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: colors.danger,
                    fontSize: 13,
                    fontWeight: 500,
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = `${colors.danger}08`)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "none")
                  }
                >
                  <ShieldCheck size={14} />
                  Suppress All
                </button>
                <button
                  onClick={() => handleBulkAction("clear")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "10px 14px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: colors.success,
                    fontSize: 13,
                    fontWeight: 500,
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = `${colors.success}08`)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "none")
                  }
                >
                  <Trash2 size={14} />
                  Clear All Flags
                </button>
              </div>
            )}
          </div>
        )}

        <span
          style={{ fontSize: 12, color: colors.textSecondary, fontWeight: 500 }}
        >
          {total} contact{total !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Contacts table */}
      {loading && contacts.length === 0 ? (
        <div
          style={{ textAlign: "center", padding: 60, color: colors.textSecondary }}
        >
          <Loader2
            size={28}
            className="spin" style={{ marginBottom: 8 }}
          />
          <div style={{ fontSize: 13 }}>Loading email health data…</div>
        </div>
      ) : contacts.length === 0 ? (
        <div
          style={{ textAlign: "center", padding: 60, color: colors.textSecondary }}
        >
          <ShieldCheck size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: colors.textSecondary,
              marginBottom: 4,
            }}
          >
            {filter === "active"
              ? "No active problems"
              : "No contacts match this filter"}
          </div>
          <div style={{ fontSize: 13 }}>
            {filter === "active"
              ? "All email delivery issues have been resolved."
              : "Try a different filter to see results."}
          </div>
        </div>
      ) : (
        <div
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}
          >
            <thead>
              <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                <th style={{ ...thStyle, width: 32 }}>
                  <input
                    type="checkbox"
                    checked={
                      selected.size === contacts.length && contacts.length > 0
                    }
                    onChange={toggleSelectAll}
                    style={{ cursor: "pointer", accentColor: colors.accent }}
                  />
                </th>
                <th style={thStyle}>Contact</th>
                <th style={thStyle}>Company</th>
                <th style={thStyle}>Problems</th>
                <th style={thStyle}>Last Issue</th>
                <th style={thStyle}>Reason</th>
                <th style={{ ...thStyle, width: 160, textAlign: "right" }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => {
                const isActing = actionLoading === c.id;
                const lastDate =
                  c.lastBounceAt ||
                  c.spamReportedAt ||
                  c.lastSoftBounceAt ||
                  c.suppressedAt;
                const reason =
                  c.lastBounceReason ||
                  c.lastSoftBounceReason ||
                  c.dropReason ||
                  c.suppressedReason ||
                  "—";
                return (
                  <tr
                    key={c.id}
                    style={{ borderBottom: `1px solid ${colors.borderLight}` }}
                  >
                    <td style={{ ...tdStyle, width: 32 }}>
                      <input
                        type="checkbox"
                        checked={selected.has(c.id)}
                        onChange={() => toggleSelect(c.id)}
                        style={{
                          cursor: "pointer",
                          accentColor: colors.accent,
                        }}
                      />
                    </td>
                    <td style={tdStyle}>
                      <div>
                        <div style={{ fontWeight: 600, color: colors.text }}>
                          {[c.firstName, c.lastName]
                            .filter(Boolean)
                            .join(" ") || "—"}
                        </div>
                        <div
                          style={{ fontSize: 11, color: colors.textSecondary }}
                        >
                          {c.email}
                        </div>
                      </div>
                    </td>
                    <td style={{ ...tdStyle, color: colors.textSecondary }}>
                      {c.company || "—"}
                    </td>
                    <td style={tdStyle}>
                      <ProblemBadges contact={c} />
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        color: colors.textSecondary,
                        whiteSpace: "nowrap",
                        fontSize: 11,
                      }}
                    >
                      {fmtDate(lastDate)}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        color: colors.textSecondary,
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: 11,
                      }}
                    >
                      <span title={reason}>{reason}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <div
                        style={{
                          display: "flex",
                          gap: 4,
                          justifyContent: "flex-end",
                        }}
                      >
                        {/* View activity */}
                        <button
                          onClick={() =>
                            navigate(
                              `/email-activity?email=${encodeURIComponent(c.email)}`,
                            )
                          }
                          title="View email activity"
                          style={actionBtnStyle}
                        >
                          <Eye size={13} color={colors.accent} />
                        </button>

                        {/* Suppress */}
                        {!c.emailSuppressed && (
                          <button
                            onClick={() => handleSuppress(c)}
                            disabled={isActing}
                            title="Suppress — block future emails"
                            style={{
                              ...actionBtnStyle,
                              background: `${colors.danger}08`,
                            }}
                          >
                            {isActing ? (
                              <Loader2
                                size={13}
                                color={colors.danger}
                                className="spin"
                              />
                            ) : (
                              <ShieldCheck size={13} color={colors.danger} />
                            )}
                          </button>
                        )}

                        {/* Clear all flags */}
                        <button
                          onClick={() => handleClear(c)}
                          disabled={isActing}
                          title="Clear all flags and restore"
                          style={{
                            ...actionBtnStyle,
                            background: `${colors.success}08`,
                          }}
                        >
                          {isActing ? (
                            <Loader2
                              size={13}
                              color={colors.success}
                              className="spin"
                            />
                          ) : (
                            <CheckCircle2 size={13} color={colors.success} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Info panel */}
      <div
        style={{
          marginTop: 24,
          padding: "16px 20px",
          background: `${colors.accent}08`,
          border: `1px solid ${colors.accent}20`,
          borderRadius: 8,
          fontSize: 12,
          color: colors.textSecondary,
          lineHeight: 1.7,
        }}
      >
        <strong style={{ color: colors.text }}>How suppression works:</strong>
        <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
          <li>
            <strong>Suppress</strong> — blocks the contact from receiving
            newsletters, invitations, and other bulk emails. The contact record
            is kept.
          </li>
          <li>
            <strong>Clear flags</strong> — removes bounce, spam, and suppression
            flags so the contact can receive emails again. Use after verifying
            the email address is valid.
          </li>
          <li>
            Hard bounces, spam reports, and suppressed contacts are
            automatically excluded from newsletter sends.
          </li>
        </ul>
      </div>    </div>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "8px 12px",
  color: colors.textSecondary,
  fontWeight: 600,
  fontSize: 11,
  textTransform: "uppercase",
  fontFamily: fonts.body,
};

const tdStyle = {
  padding: "10px 12px",
  color: colors.text,
};

const actionBtnStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 30,
  height: 30,
  borderRadius: 6,
  border: `1px solid ${colors.border}`,
  background: "none",
  cursor: "pointer",
};
